import { Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { ExecuteReportValues } from "shared/types";
import {
  useGetAllCompaniesQuery,
  useGetMaterialCategoriesQuery,
} from "shared/api";
import { selectFiltering } from "shared/helpers";
import { Colors } from "shared/themes";
import { Loader } from "shared/ui";

interface StockFilterProps {}

const StockFilter: FC<StockFilterProps> = () => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });
  const { setFieldValue, values } = useFormikContext<ExecuteReportValues>();

  const {
    data: companies,
    isLoading,
    isFetching,
  } = useGetAllCompaniesQuery({});

  const {
    data: materialCategories,
    isLoading: isLoadingMaterialCategories,
    isFetching: isFetchingMaterialCategories,
  } = useGetMaterialCategoriesQuery({});

  const adaptedCompanies = useMemo(
    () =>
      companies?.map((company) => ({
        label: company.name,
        value: company.id,
      })) ?? [],
    [companies]
  );

  const adaptedMaterialCategories = useMemo(
    () =>
      Array.isArray(materialCategories)
        ? materialCategories.map((category) => ({
            label: category.name,
            value: category.id,
          }))
        : [],
    [materialCategories]
  );

  if (
    isLoading ||
    isFetching ||
    isLoadingMaterialCategories ||
    isFetchingMaterialCategories
  ) {
    return <Loader type="fullscreen-backdrop" />;
  }

  const handleCompanyChange = (value: number) => {
    setFieldValue("company_id", value);
  };

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex gap-x-2">
        <Title level={5} className="!w-[140px] !m-0 !text-start">
          {t("selectCompany")}
        </Title>
        {" - "}
        <Select
          loading={isLoading}
          value={values.company_id ?? undefined}
          placeholder={t("selectCompany")}
          options={adaptedCompanies}
          onChange={handleCompanyChange}
          className="w-auto"
          size="small"
          showSearch
          filterOption={(input, option) => selectFiltering(input, option)}
        />

        <Title level={5} className="!w-[183px] !m-0 !text-start">
          {t("selectMaterialCategory")}
        </Title>
        {" - "}
        <Select
          mode="multiple"
          loading={isLoadingMaterialCategories}
          value={values.material_category_id_list || []}
          placeholder={t("selectMaterialCategory")}
          options={adaptedMaterialCategories}
          onChange={(value) =>
            setFieldValue("material_category_id_list", value)
          }
          size="small"
          showSearch
          filterOption={(input, option) => selectFiltering(input, option)}
          className="w-[250px]"
        />
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { StockFilter };
